import { useState, useEffect, useRef } from "react";
import ProductHeader from "../../Components/ProductsPages/ProductHeader";
import ProductsHeroSection from "../../Components/ProductsPages/ProductsHeroSection";
import ProductFooter from "../../Components/ProductsPages/ProductFooter";
import RequestDemo from "../../Components/ProductsPages/RequestDemo";
import QSleeveImage from "../../Images/videothumbnail.jpg";
import BackgroundGradient from '../../Images/BackgroundGradient.png'
import BackgroundGradientSmall from '../../Images/BackgroundGradientSmall.png'
import VideoComponent from "../../Components/VideoComponent";
import { KeyFeatures } from "../../Components/KeyFeatures";
import { ProductTable } from "../../Components/ProductsPages/ProductTable";
import QCLUSTER from "../../../src/Images/Boxes/QCLUSTER_P.png";
import { currentSite } from "../../Config/siteConfig";


export default function QCluster() {
  const requestDemoRef = useRef(null);
  const architectureRef = useRef(null);

  const [backgroundImage, setBackgroundImage] = useState(``);

  useEffect(() => {
    const updateBackgroundImage = () => {
      if (window.innerWidth < 640) {
        setBackgroundImage(`url(${BackgroundGradientSmall})`);
      } else {
        setBackgroundImage(`url(${BackgroundGradient})`);
      }
    };

    updateBackgroundImage();

    window.addEventListener('resize', updateBackgroundImage);

    return () => window.removeEventListener('resize', updateBackgroundImage);
  }, []);

  const scrollToRequestDemo = () => {
    requestDemoRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToArchitecture = () => {
    architectureRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const keyFeatures = [
    "GUI for Quantum Learning: An intuitive graphical interface tailored for educators, learners, and researchers—designed to simplify building, running, and understanding quantum algorithms.",
    "Noise Modelling: Explore noise-integrated approximate simulations to understand real quantum processor behavior in both conceptual and practical contexts.",
    "Algorithm Development and Application Prototyping: Build, test, and refine quantum applications under ideal and noisy conditions with approximate QPU performance simulations.",
    "Open and Scalable Simulation Infrastructure: Seamlessly scale from basic learning modules to large-scale quantum algorithms using a hybrid HPC backend.",
    "Quantum Algorithm Learning: Learn and implement foundational quantum algorithms on scalable qubit systems through guided modules and circuit-building tools.",
    "Simulation of Noise Effects: Gain intuition on how gate errors, decoherence, and readout noise impact quantum computations.",
    "Quantum Error Correction (QEC) Learning: Simulate and analyze logical qubit encoding/decoding, evaluate gate fidelity, and assess performance under configurable noise models.",
    "Complete Spectrum QIP: Explore the full lifecycle of quantum algorithms—from design to execution and evaluation—across ideal and noisy environments.",
    "Quantum Application Prototyping: Build, test, and debug scalable quantum workflows up to 32 qubits (noiseless) and 9 qubits (with noise) using hybrid CPU-GPU backends.",
    "QPU Performance Approximation: Simulate realistic quantum hardware behavior using configurable noise density matrices for NISQ-era research.",
    "Resource-Conscious Algorithm Scaling: Understand how CPU/GPU resources influence the scalability, depth, and execution limits of quantum simulations.",
  ];
  
  const specifications = [
            ["Specification", "Description"],
            [
              "Computing Infrastructure",
              "CPU & GPU (T4, V100, A100, H100 supported)",
            ],
            ["Simulation Types", "Statevector, Noisy Density Matrix"],
            [
              "Supported Algorithms",
              "Complete spectrum: Grover’s, QFT, VQE, QAOA, QNNs, Quantum Chemistry and more",
            ],
            ["Noise Models", "Gate errors, decoherence, readout noise"],
            [
              "Qubits Simulatable",
              "Up to 32 (noiseless), up to 9 (with noise)",
            ],
            [
              "GUI Capabilities",
              "Drag-and-drop circuit builder and Educational Mode for Quantum Learning",
            ],
            [
              "Educational Mode",
              "Quantum algorithms tutorials, circuit design tasks, guided modules for approximation of QPU behavior",
            ],
            [
              "Output",
              "Probability graph distribution, Basis state probability distribution, qubit representation on Bloch Sphere, analysis of Bloch sphere, and intuitive interpretation of quantum algorithms",
            ],
            [
              "Multi-user Environment",
              "Classroom/Lab deployment ready, JupyterHub compatible",
            ],
          ]




  return (
    <div className="w-full  flex flex-col gap-0 items-center justify-center bg-primary7">
      <div
        className="w-full width flex flex-col gap-0 items-center justify-center bg-contain bg-repeat"
        style={{ backgroundImage: backgroundImage }}
      >
        <ProductHeader scrollToRequestDemo={scrollToRequestDemo} />
        <div className="w-full h-[25rem] ">
          <ProductsHeroSection
            title1="Learn Quantum Computing"
            title2="In a Practical Way"
            content="Simulate, analyze, and understand quantum algorithms on realistic hardware models using hybrid CPU-GPU acceleration and advanced noise modeling. "
            scrollToArchitecture={scrollToArchitecture}
          />
        </div>

        <div className="w-full bg-primary7 width md:px-10  flex flex-col items-center justify-center pb-10 gap-1">
          <div className="w-[90%] text-center md:text-left text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem] font-RobotoBold text-white leading-tight px-3 ">
            <span className="wordGradient">Quantum Computing Simulator</span>
          </div>

          <div className="w-[90%] h-full  grid grid-cols-1 xl:grid-cols-2  place-items-start p-3">
            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                Q-Cluster is a pioneering quantum simulation platform designed
                to operate over hybrid CPU-GPU computing infrastructure. Built
                to go beyond ideal quantum processor simulations to
                understanding quantum algorithm performance on realistic,
                imperfect quantum systems through QPU performance approximation.
                Tailored for educators, learners, and researchers, it supports
                both ideal state vector simulation and noise modeled execution.
                Whether you’re teaching basic quantum gates or building your own
                quantum applications, Q-Cluster is the platform to use.
              </div>
            </div>

            <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
              Q-Cluster is a pioneering quantum simulation platform designed to
              operate over hybrid CPU-GPU computing infrastructure. Built to go
              beyond ideal quantum processor simulations to understanding
              quantum algorithm performance on realistic, imperfect quantum
              systems through QPU performance approximation. Tailored for
              educators, learners, and researchers, it supports both ideal state
              vector simulation and noise modeled execution. Whether you’re
              teaching basic quantum gates or building your own quantum
              applications, Q-Cluster is the platform to use.
            </div>
          </div>
          <div className="w-[90%] h-full flex flex-col xl:flex-row justify-center items-center gap-10  p-3">
            <div className="w-full flex flex-col items-center justify-center">
              <img
                className="mt-3   object-contain rounded-lg md:rounded-2xl"
                alt="QCLUSTER"
                src={QCLUSTER}
              ></img>
              <p className="w-full text-center text-xs italic text-white/70 mt-2">
                Figure 1: Quantum Computing Simulator
              </p>
            </div>
            <div className="w-full  font-RobotoRegular  px-3">
              <ProductTable title="Specifications" data={specifications} />
            </div>
          </div>
        <div className="w-[90%] bg-primary7 width md:px-10  flex flex-col lg:flex-row items-start justify-center  gap-1">
          <KeyFeatures
            title="Key Features"
            features={keyFeatures.slice(0, 4)}
          />
          <KeyFeatures
            title="Experimental Outcomes"
            features={keyFeatures.slice(4, 12)}
          />
        </div>
        </div>
        {currentSite === "site2" && (
          <VideoComponent
            video={"https://thequantum.ai/videos/QClusterVideo.mp4"}
          />
        )}
        {currentSite === "site1" && (
          <VideoComponent
            video={"https://qulabs.ai/videos/QClusterVideo.mp4"}
          />
        )}
        <div
          className="w-full bg-primary7 width flex flex-col items-center justify-center gap-2 "
          ref={requestDemoRef}
        >
          <RequestDemo />
        </div>
        <ProductFooter />
      </div>
    </div>
  );
}


